import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Drawer,
  Box,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  DrawerProps,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Divider from "../../../components/Divider";
import useAuthValue from "../../authModule/hooks/useAuthValue";
import useFilterValue, {
  getFilterValue,
  updateFilterValue,
} from "../hooks/useFilterValue";
import { initialFilterState } from "../observables/filterState$";
import { useGetCasesQuery } from "../hooks/useGetCasesQuery";
import { formatDate } from "../../../helpers/dateHelper";
import "react-day-picker/dist/style.css";
import "../../../styles/calendar.css";
import calculateCalendarDefaults, {
  getLast7DaysRange,
} from "../../../helpers/calculateCalendarDefaults";
import useEventEmitter, { emitEvent } from "../../../hooks/useEventEmitter";
import Button from "../../../components/Button";
import useSelectedHospitalValue from "../../hospitalModule/hooks/useSelectedHospitalValue";
import useCreateSavedFilterMutation from "../hooks/useCreateSavedFilterMutation";
import useUpdateSavedFilterMutation from "../hooks/useUpdateSavedFilterMutation";
import CustomTabs from "../../../components/CustomTabs";
import DashboardFiltersTab from "./DashboardFiltersTab";
import DashboardSavedFiltersTab from "./DashboardSavedFiltersTab";
import Icons from "../../../assets/Icons";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import { useGetFilterOTSQuery } from "../hooks/useGetFilterOTSQuery";
import { useGetFilterProceduresQuery } from "../hooks/useGetFilterProceduresQuery";
import useGetSavedFiltersQuery from "../hooks/useGetSavedFiltersQuery";
import { useGetFilterSurgeonQuery } from "../hooks/useGetFilterSurgeonQuery";
import { useGetFilterCurculatingNurseQuery } from "../hooks/useGetFilterCurculatingNurseQuery";
import { useGetFilterAnesthesiologistsQuery } from "../hooks/useGetFilterAnesthesiologistsQuery";
import { useGetFilterDelayCategoriesQuery } from "../hooks/useGetFilterDelayCategoriesQuery";
import { useGetFilterCaseIdQuery } from "../hooks/useGetFilterCaseIdQuery";
import { updateFilterLabels } from "../observables/filterLabels$";
import { USER_ROLES } from "../../userModule/types/CommonTypes";

const useSmartFilter = (
  listData: any[] | undefined,
  uiList: any[],
  fieldName: string,
  getValues: any,
  setValue: any,
  idKey: string = "key",
  dependencies: any[] = [],
) => {
  const prevDepsRef = useRef<any[]>(dependencies);

  useEffect(() => {
    const hasClearedDependency = dependencies.some((dep, index) => {
      const prevDep = prevDepsRef.current[index];
      const wasPresent = Array.isArray(prevDep)
        ? prevDep.length > 0
        : !!prevDep;
      const isPresent = Array.isArray(dep) ? dep.length > 0 : !!dep;
      return wasPresent && !isPresent;
    });

    prevDepsRef.current = dependencies;

    if (hasClearedDependency) {
      setValue(fieldName, []);
      return;
    }

    if (!listData) return;
    const currentVal = getValues(fieldName);
    if (!currentVal || (Array.isArray(currentVal) && currentVal.length === 0))
      return;
    const validKeys = new Set(uiList.map((item) => item[idKey]));
    const currentArray = Array.isArray(currentVal) ? currentVal : [currentVal];
    const newArray = currentArray.filter((val: any) => validKeys.has(val));
    if (newArray.length !== currentArray.length) {
      setValue(fieldName, newArray);
    }
  }, [
    listData,
    uiList,
    fieldName,
    getValues,
    setValue,
    idKey,
    dependencies,
  ]);
};

const DASHBOARD_FILTER_DRAWER_EVENT = "DASHBOARD_FILTER_DRAWER_EVENT";
type KnownAdvanceFilterField =
  | "otNames"
  | "procedureIds"
  | "attendingSurgeonNames"
  | "circulatingNurses"
  | "anesthesiologists"
  | "caseStartDelays"
  | "caseOrder";
export type SAVED_FILTER = {
  uuid: string;
  filterName: string;

  dateRange?: {
    startDate?: string | Date;
    endDate?: string | Date;
    from?: string | Date;
    to?: string | Date;
    start?: string | Date;
    end?: string | Date;
  };

  otNames?: string[]; // labels/names
  procedureIds?: string[];
  primarySurgeons?: string[];
  circulatingNurses?: string[];
  anesthesiologists?: string[];
  caseStartDelays?: string[];
  caseOrder?: "first" | "last" | "";
  autoClosed?: boolean; // boolean from API
  inconsistentTimestamps?: boolean; // boolean from API
  antibioticUsed?: boolean; // boolean from API
  caseIds?: string[]; // adjust if your API sends different type
  isDefault?: boolean;
  advanceFilters?: Array<{
    field: KnownAdvanceFilterField;
    operator?: string;
    values?: Array<{ id: string; name?: string }>;
  }>;
};
type PendingSavedFilter = SAVED_FILTER & {
  __dateRange: { from: Date; to: Date };
};

export function toggleDashboardFilterDrawer() {
  emitEvent(DASHBOARD_FILTER_DRAWER_EVENT);
}

const tabData = [
  { id: "Filters", label: "Filters" },
  {
    id: "Saved_Filters",
    label: "Saved Filters",
  },
];

const sortAlpha = (a: string, b: string) =>
  a.localeCompare(b, undefined, { sensitivity: "base", numeric: true });

const toCsv = (arr?: string[]) =>
  Array.isArray(arr) && arr.length
    ? [...arr].map(String).sort(sortAlpha).join(",")
    : undefined;

const toKey = (arr?: string[]) =>
  Array.isArray(arr) && arr.length
    ? [...arr].map(String).sort(sortAlpha).join(",")
    : "";

const joinCsv = (arr?: string[]) =>
  Array.isArray(arr) && arr.length ? arr.join(",") : undefined;

const assignIf = (p: any, key: string, value: any) => {
  if (value !== undefined && value !== null && value !== "") p[key] = value;
};

const assignCsvIf = (p: any, key: string, arr?: string[]) => {
  const v = joinCsv(arr);
  if (v) p[key] = v;
};
 const toNullableBool = (v: any): boolean | null => {
  if (v === "true") return true;
  if (v === "false") return false;
  return null;
};

 const toNullableCaseOrder = (v: any): "first" | "last" | null => {
  const isValid = v === "first" || v === "last";
  return isValid ? v : null;
};

 const toNullIfEmptyArr = <T,>(arr: T[] | undefined | null) =>
  Array.isArray(arr) && arr.length ? arr : null;

const mapIdsToLabelsOrNull = (
  ids: any,
  labelMap: Map<string, string>,
): string[] | null => {
  if (!Array.isArray(ids) || ids.length === 0) return null;
  const labels = ids
    .map((id) => labelMap.get(String(id)) ?? "")
    .map((s) => String(s).trim())
    .filter(Boolean);
  return labels.length ? labels : null;
};

const mapProcIdsToLabelsOrNull = (
  procIds: any,
  labelMap: Map<string, string>,
): string[] | null => {
  if (!Array.isArray(procIds) || procIds.length === 0) return null;
  const labels = procIds
    .map((id) => labelMap.get(String(id)) ?? String(id))
    .map((s) => String(s).trim())
    .filter(Boolean);
  return labels.length ? labels : null;
};

const isBoolString = (v: any) => v === "true" || v === "false";

const getRangeKey = (from: Date, to: Date) =>
  `${formatDate(from)}_${formatDate(to)}`;

const needsOtsButMissing = (pending: PendingSavedFilter, otsData?: any[]) =>
  (pending.otNames?.length ?? 0) > 0 && !otsData;

const needsProceduresButMissing = (
  pending: PendingSavedFilter,
  proceduresData?: any[],
) => (pending.procedureIds?.length ?? 0) > 0 && !proceduresData;

const resolveOtIdsSafe = (
  pending: PendingSavedFilter,
  otsData: any[] | undefined,
  resolveFn: (names: string[], data: any[]) => string[],
) => {
  const names = pending.otNames ?? [];
  const ids = resolveFn(names, otsData ?? []);
  return { names, ids };
};

const resolveProcedureIdsFromSaved = (
  savedProcedures: string[],
  proceduresData: any[],
) => {
  if (!Array.isArray(savedProcedures) || savedProcedures.length === 0) {
    return [];
  }

  const wantedByLabel = new Set(
    savedProcedures.map((x) => String(x).trim().toLowerCase()),
  );

  const matchesByLabel = (proceduresData || [])
    .filter((p: any) => wantedByLabel.has(String(p.label).trim().toLowerCase()))
    .map((p: any) => p.value);

  if (matchesByLabel.length) return matchesByLabel;

  const wantedByValue = new Set(savedProcedures.map((x) => String(x)));

  return (proceduresData || [])
    .filter((p: any) => wantedByValue.has(String(p.value)))
    .map((p: any) => p.value);
};

const buildNextStateFromPending = (
  pending: PendingSavedFilter,
  resolvedOtIds: string[],
  resolvedProcedureIds: string[],
  excludeState: any,
) => ({
  ...initialFilterState,
  dateRange: pending.__dateRange,
  ots: resolvedOtIds,
  procedureIds:
    resolvedProcedureIds.length > 0
      ? resolvedProcedureIds
      : (pending.procedureIds ?? []),
  attendingSurgeonNames: pending.primarySurgeons ?? [],
  nurseIds: pending.circulatingNurses ?? [],
  assignedAnesthesiologist: pending.anesthesiologists ?? [],
  caseStartDelay: pending.caseStartDelays ?? [],
  caseOrder: pending.caseOrder ?? "",
  autoClosed: pending.autoClosed != null ? String(pending.autoClosed) : "",
  inconsistentTimestamps:
    pending.inconsistentTimestamps != null
      ? String(pending.inconsistentTimestamps)
      : "",
  antibioticUsed: pending.antibioticUsed != null ? String(pending.antibioticUsed) : "",
  caseIds: pending.caseIds ?? [],
  ...excludeState,
  advanceFilters: pending.advanceFilters ?? [],
});

const DashboardFilterDrawer = () => {
  const [isApplying, setIsApplying] = useState(false);
  const { t } = useTranslation();
  const currentHospital = useSelectedHospitalValue();
  const {
    tenantId: authTenantId,
    hospitalId: authHospitalId,
    user,
  } = useAuthValue();
  const hospitalId = currentHospital?.uuid ?? authHospitalId;
  const tenantId = currentHospital?.tenantId ?? authTenantId;
  const { dateRange: dashboardFilterDateRange } = useFilterValue();
  const isSurgeon = user?.role === USER_ROLES.SURGEON;
  const surgeonSelfName = useMemo(() => {
    if (!isSurgeon) return null;
    const name = `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim();
    return name || null;
  }, [isSurgeon, user?.firstName, user?.lastName]);

  // Apply the surgeon's own name to the global dashboard filter as soon as it's
  // known, so their data shows up immediately without opening the filter drawer.
  useEffect(() => {
    if (!surgeonSelfName) return;
    const current = getFilterValue().attendingSurgeonNames;
    if (Array.isArray(current) && current.length === 1 && current[0] === surgeonSelfName) {
      return;
    }
    updateFilterValue({ attendingSurgeonNames: [surgeonSelfName] });
  }, [surgeonSelfName]);

  //new change
  const draftFiltersRef = useRef<any>(getFilterValue());

  const setDraftFilters = useCallback((next: any) => {
    draftFiltersRef.current = next;
  }, []);
  const { mutate: updateSavedFilter } = useUpdateSavedFilterMutation(
    tenantId,
    hospitalId,
  );

  const [updateSavedFilterId, setUpdateSavedFilterId] = useState<string | null>(
    null,
  );

  const [applyCount, setApplyCount] = useState(0);

  const [activeTab, setActiveTab] = useState<string | number>("Filters");
  const [filterValueDropdown, setFilterValueDropdown] = useState<any[]>([]);
  const CASE_ORDER_OPTIONS = useMemo(
    () => [
      { key: "first", value: "First Case" },
      { key: "last", value: "Last Case" },
    ],
    [],
  );

  useEffect(() => {
    setFilterValueDropdown((prev) => [
      ...prev.filter((f) => f.key !== "caseOrder"),
      {
        key: "caseOrder",
        label: "Case Order",
        value: CASE_ORDER_OPTIONS,
        isMulti: false,
      },
    ]);
  }, [CASE_ORDER_OPTIONS]);

  const [selectedAdvancedFilters, setSelectedAdvancedFilters] = useState(() => {
    const current = getFilterValue().advanceFilters;
    if (current && current.length > 0) {
      return current.map((af: any) => {
        const field = af.field || af.filterType;
        const values =
          (af.values || af.selectedValues)?.map((v: any) => v.id ?? v) || [];
        return {
          filterType: field,
          condition: af.operator || af.condition || "notEquals",
          selectedValues: field === "caseOrder" ? (values[0] ?? "") : values,
        };
      });
    }
    return [
      {
        filterType: "",
        condition: "",
        selectedValues: [],
      },
    ];
  });
  const [filtersIndexList, setFiltersIndexList] = useState<number[]>(() => {
    const current = getFilterValue().advanceFilters;
    if (current && current.length > 0) return current.map((_, i) => i);
    return [0];
  });
  const handleDrawerClose: DrawerProps["onClose"] = (_event, reason) => {
    if (reason === "backdropClick") return;
    setIsFilterDrawerOpen(false);
  };

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [quickDays, setQuickDays] = useState<number | null>(null);

  useEventEmitter(DASHBOARD_FILTER_DRAWER_EVENT, () => {
    setIsFilterDrawerOpen((prev) => !prev);
  });
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    unregister,
  } = useForm({
    defaultValues: getFilterValue(),
  });
  const [dateParams, setDateParams] = useState<DateParams | undefined>(
    undefined,
  );
  type DateParams = { startDate: string; endDate: string };
  const isDateReady = !!dateParams?.startDate && !!dateParams?.endDate;

  //Watched Values
  const watchedDateRange = useWatch({
    control,
    name: "dateRange",
  });

  const rawDateParams = useMemo<DateParams | undefined>(() => {
    if (!watchedDateRange?.from || !watchedDateRange?.to) return undefined;
    return {
      startDate: formatDate(watchedDateRange.from as Date),
      endDate: formatDate(watchedDateRange.to as Date),
    };
  }, [watchedDateRange?.from, watchedDateRange?.to]);

  useEffect(() => {
    if (!rawDateParams) return;

    const t = window.setTimeout(() => {
      setDateParams(rawDateParams);
    }, 300);

    return () => window.clearTimeout(t);
  }, [rawDateParams]);

  // for Procedure Selection flow
  const watchedOts = useWatch({
    control,
    name: "ots",
  });

  const watchedProcedures = useWatch({
    control,
    name: "procedureIds",
  });

  const watchedSurgeons = useWatch({
    control,
    name: "attendingSurgeonNames",
  });

  useEffect(() => {
    if (!surgeonSelfName) return;
    const current = Array.isArray(watchedSurgeons) ? watchedSurgeons : [];
    if (current.length === 1 && current[0] === surgeonSelfName) return;
    setValue("attendingSurgeonNames", [surgeonSelfName]);
  }, [surgeonSelfName, watchedSurgeons, setValue]);
  const watchedCirculatingNurse = useWatch({
    control,
    name: "nurseIds",
  });
  const watchedAnesthesiologist = useWatch({
    control,
    name: "assignedAnesthesiologist",
  });
  const watchedCaseStartDelay = useWatch({
    control,
    name: "caseStartDelay",
  });

  const dateBase = useMemo(() => {
    if (!dateParams) return undefined;
    return { startDate: dateParams.startDate, endDate: dateParams.endDate };
  }, [dateParams]);

  // level 0: OT list (depends ONLY on date)
  const otParams = useMemo(() => {
    if (!dateBase) return undefined;
    return { ...dateBase };
  }, [dateBase]);

  const procedureParams = useMemo(() => {
    if (!dateBase) return undefined;
    const p: any = { ...dateBase };
    const ots = toCsv(watchedOts);
    if (ots) p.ots = ots;
    return p;
  }, [dateBase, watchedOts]);

  // level 2: Surgeon  (date + OT + procedure) ✅ NOT dependent on surgeon selection
  const surgeonParams = useMemo(() => {
    if (!dateBase) return undefined;
    const p: any = { ...dateBase };
    const ots = toCsv(watchedOts);
    const procs = toCsv(watchedProcedures);
    if (ots) p.ots = ots;
    if (procs) p.procedureIds = procs;
    return p;
  }, [dateBase, watchedOts, watchedProcedures]);

  const nurseParams = useMemo(() => {
    if (!dateBase) return undefined;
    const p: any = { ...dateBase };
    const ots = toCsv(watchedOts);
    const procs = toCsv(watchedProcedures);
    const surg = toCsv(watchedSurgeons);
    if (ots) p.ots = ots;
    if (procs) p.procedureIds = procs;
    if (surg) p.attendingSurgeonNames = surg;
    return p;
  }, [dateBase, watchedOts, watchedProcedures, watchedSurgeons]);

  const anesParams = useMemo(() => {
    if (!dateBase) return undefined;
    const p: any = { ...dateBase };
    const ots = toCsv(watchedOts);
    const procs = toCsv(watchedProcedures);
    const surg = toCsv(watchedSurgeons);
    const nurse = toCsv(watchedCirculatingNurse);
    if (ots) p.ots = ots;
    if (procs) p.procedureIds = procs;
    if (surg) p.attendingSurgeonNames = surg;
    if (nurse) p.nurseIds = nurse;
    return p;
  }, [
    dateBase,
    watchedOts,
    watchedProcedures,
    watchedSurgeons,
    watchedCirculatingNurse,
  ]);

  const delayParams = useMemo(() => {
    if (!dateBase) return undefined;
    const p: any = { ...dateBase };
    const ots = toCsv(watchedOts);
    const procs = toCsv(watchedProcedures);
    const surg = toCsv(watchedSurgeons);
    const nurse = toCsv(watchedCirculatingNurse);
    const anes = toCsv(watchedAnesthesiologist);
    if (ots) p.ots = ots;
    if (procs) p.procedureIds = procs;
    if (surg) p.attendingSurgeonNames = surg;
    if (nurse) p.nurseIds = nurse;
    if (anes) p.assignedAnesthesiologist = anes;
    return p;
  }, [
    dateBase,
    watchedOts,
    watchedProcedures,
    watchedSurgeons,
    watchedCirculatingNurse,
    watchedAnesthesiologist,
  ]);

  const caseIdParams = useMemo(() => {
    if (!dateBase) return undefined;
    const p: any = { ...dateBase };
    const ots = toCsv(watchedOts);
    const procs = toCsv(watchedProcedures);
    const surg = toCsv(watchedSurgeons);
    const nurse = toCsv(watchedCirculatingNurse);
    const anes = toCsv(watchedAnesthesiologist);
    const delay = toCsv(watchedCaseStartDelay);
    if (ots) p.ots = ots;
    if (procs) p.procedureIds = procs;
    if (surg) p.attendingSurgeonNames = surg;
    if (nurse) p.nurseIds = nurse;
    if (anes) p.assignedAnesthesiologist = anes;
    if (delay) p.delayCategories = delay;
    return p;
  }, [
    dateBase,
    watchedOts,
    watchedProcedures,
    watchedSurgeons,
    watchedCirculatingNurse,
    watchedAnesthesiologist,
    watchedCaseStartDelay,
  ]);

  const enabled = isDateReady;

  const { data: otsData } = useGetFilterOTSQuery(otParams, enabled);

  const { data: procedureListData } = useGetFilterProceduresQuery(
    procedureParams,
    enabled,
  );

  const { data: surgeonListData } = useGetFilterSurgeonQuery(
    surgeonParams,
    enabled,
  );

  const { data: circulationNurseListData } = useGetFilterCurculatingNurseQuery(
    nurseParams,
    enabled,
  );

  const { data: anesthesiologistListData } = useGetFilterAnesthesiologistsQuery(
    anesParams,
    enabled,
  );

  const { data: caseStartDelayListData } = useGetFilterDelayCategoriesQuery(
    delayParams,
    enabled,
  );

  const { data: caseIdListData } = useGetFilterCaseIdQuery(
    caseIdParams,
    enabled,
  );

  const [pendingSavedFilter, setPendingSavedFilter] =
    useState<PendingSavedFilter | null>(null);

  const lastOtsRef = useRef<string>("");

  useEffect(() => {
    if (!watchedOts?.length) return;

    const key = toKey(watchedOts);
    if (lastOtsRef.current === key) return;

    lastOtsRef.current = key;
  }, [watchedOts]);

  const { data: savedFiltersData } = useGetSavedFiltersQuery(
    tenantId,
    hospitalId,
  );

  type IdLike =
    | string
    | { key?: string; value?: string }
    | Array<string | { key?: string; value?: string }>;

  const normalizeIds = useCallback(
    (value: IdLike | null | undefined): string[] => {
      if (value == null) return [];

      if (typeof value === "string") return [value];

      if (Array.isArray(value)) {
        return value
          .map((v) => (typeof v === "string" ? v : v?.key ?? v?.value))
          .filter(Boolean) as string[];
      }

      return [value.key ?? value.value].filter(Boolean) as string[];
    },
    [],
  );

  //apply saved filters
  const savedFiltersCount = savedFiltersData?.length ?? 0;
  const MAX_SAVED_FILTERS = 5;

  const isMaxLimitReached = savedFiltersCount >= MAX_SAVED_FILTERS;

  const { filters, savedFilters } = useMemo(() => {
    return {
      filters: activeTab === "Filters",
      savedFilters: activeTab === "Saved_Filters",
    };
  }, [activeTab]);

  const otsList = useMemo(() => {
    return (
      otsData?.map((ot: any) => ({
        key: ot.value,
        value: ot.label,
        isCount: true,
        count: ot.count,
      })) ?? []
    );
  }, [otsData]);

  const proceduresList = useMemo(() => {
    if (!procedureListData || procedureListData.length === 0) {
      return [];
    }
    return procedureListData?.map((pr: any) => ({
      key: pr.value,
      value: pr.label,
      isCount: true,
      count: pr.count,
    }));
  }, [procedureListData]);

  const isValidValue = (v?: string) =>
    !!v?.trim() && v.trim().toLowerCase() !== "undefined";

  const surgeonsList = useMemo(
    () =>
      surgeonListData
        ?.filter((s: any) => isValidValue(s?.value))
        ?.map((s: any) => ({
          key: s.value.trim(),
          value: s.label?.trim(),
          isCount: true,
          count: s.count,
        })) ?? [],
    [surgeonListData],
  );

  const circulatingNurseList = useMemo(() => {
    return (
      circulationNurseListData
        ?.filter((n: any) => isValidValue(n?.value))
        ?.map((n: any) => ({
          key: n.value.trim(),
          value: n.label.trim(),
          isCount: true,
          count: n.count,
        })) ?? []
    );
  }, [circulationNurseListData]);

  const anesthesiologistList = useMemo(() => {
    return (
      anesthesiologistListData
        ?.filter((n: any) => isValidValue(n?.value))
        ?.map((n: any) => ({
          key: n.value.trim(),
          value: n.label.trim(),
          isCount: true,
          count: n.count,
        })) ?? []
    );
  }, [anesthesiologistListData]);

  const caseStartDelayList = useMemo(() => {
    return (
      caseStartDelayListData?.map((delay: any) => ({
        key: (delay.value ?? delay.label)?.trim(),
        value: (delay.label ?? delay.value)?.trim(),
        isCount: true,
        count: delay.count,
      })) ?? []
    );
  }, [caseStartDelayListData]);

  const caseIdData = useMemo(() => {
    return (
      caseIdListData?.map((item: any) => ({
        key: item.value,
        value: item.label,
      })) ?? []
    );
  }, [caseIdListData]);

  // Smart Filtering
  const otsDeps = useMemo(() => [watchedOts], [watchedOts]);
  const procDeps = useMemo(
    () => [watchedOts, watchedProcedures],
    [watchedOts, watchedProcedures],
  );
  const surgDeps = useMemo(
    () => [watchedOts, watchedProcedures, watchedSurgeons],
    [watchedOts, watchedProcedures, watchedSurgeons],
  );
  const nurseDeps = useMemo(
    () => [
      watchedOts,
      watchedProcedures,
      watchedSurgeons,
      watchedCirculatingNurse,
    ],
    [watchedOts, watchedProcedures, watchedSurgeons, watchedCirculatingNurse],
  );
  const anesDeps = useMemo(
    () => [
      watchedOts,
      watchedProcedures,
      watchedSurgeons,
      watchedCirculatingNurse,
      watchedAnesthesiologist,
    ],
    [
      watchedOts,
      watchedProcedures,
      watchedSurgeons,
      watchedCirculatingNurse,
      watchedAnesthesiologist,
    ],
  );

  useSmartFilter(
    procedureListData,
    proceduresList,
    "procedureIds",
    getValues,
    setValue,
    "key",
    otsDeps,
  );
  useSmartFilter(
    surgeonListData,
    surgeonsList,
    "attendingSurgeonNames",
    getValues,
    setValue,
    "key",
    procDeps,
  );
  useSmartFilter(
    circulationNurseListData,
    circulatingNurseList,
    "nurseIds",
    getValues,
    setValue,
    "key",
    surgDeps,
  );
  useSmartFilter(
    anesthesiologistListData,
    anesthesiologistList,
    "assignedAnesthesiologist",
    getValues,
    setValue,
    "key",
    nurseDeps,
  );
  useSmartFilter(
    caseStartDelayListData,
    caseStartDelayList,
    "caseStartDelay",
    getValues,
    setValue,
    "key",
    anesDeps,
  );
  useSmartFilter(
    caseIdListData,
    caseIdData,
    "caseIds",
    getValues,
    setValue,
    "value",
    anesDeps,
  );

  const filterState = useFilterValue();

  // OTs map
  useEffect(() => {
    if (!otsData?.length) return;
    updateFilterLabels({
      otById: Object.fromEntries(otsData.map((x: any) => [x.value, x.label])),
    });
  }, [otsData]);

  // Procedures map
  useEffect(() => {
    if (!procedureListData?.length) return;
    updateFilterLabels({
      procedureById: Object.fromEntries(
        procedureListData.map((x: any) => [x.value, x.label]),
      ),
    });
  }, [procedureListData]);

  // Surgeons map (optional, you already show names)
  useEffect(() => {
    if (!surgeonListData?.length) return;
    updateFilterLabels({
      surgeonById: Object.fromEntries(
        surgeonListData.map((x: any) => [x.value, x.label]),
      ),
    });
  }, [surgeonListData]);

  // Nurses map
  useEffect(() => {
    if (!circulationNurseListData?.length) return;
    updateFilterLabels({
      nurseById: Object.fromEntries(
        circulationNurseListData.map((x: any) => [x.value, x.label]),
      ),
    });
  }, [circulationNurseListData]);

  // Anesthesiologists map
  useEffect(() => {
    if (!anesthesiologistListData?.length) return;
    updateFilterLabels({
      anesthById: Object.fromEntries(
        anesthesiologistListData.map((x: any) => [x.value, x.label]),
      ),
    });
  }, [anesthesiologistListData]);

  // Delay map
  useEffect(() => {
    if (!caseStartDelayListData?.length) return;
    updateFilterLabels({
      delayById: Object.fromEntries(
        caseStartDelayListData.map((x: any) => [
          x.value ?? x.label,
          x.label ?? x.value,
        ]),
      ),
    });
  }, [caseStartDelayListData]);

  const shouldFetchCases =
    applyCount > 0 &&
    !!filterState?.dateRange?.from &&
    !!filterState?.dateRange?.to &&
    Array.isArray(filterState?.ots) &&
    filterState.ots.length > 0;

  const buildCasesParams = (
    filterState: any,
    shouldFetchCases: boolean,
    normalizeIds: (v: any) => string[],
  ) => {
    if (!shouldFetchCases) return {};

    const p: any = {
      startDate: formatDate(filterState.dateRange.from as Date),
      endDate: formatDate(filterState.dateRange.to as Date),
      ots: joinCsv(filterState.ots),
      isDashboard: true,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    // include
    assignIf(p, "caseOrder", filterState.caseOrder);
    assignCsvIf(p, "procedureIds", filterState.procedureIds);
    assignCsvIf(p, "attendingSurgeonNames", filterState.attendingSurgeonNames);
    assignCsvIf(p, "nurseIds", filterState.nurseIds);
    assignCsvIf(
      p,
      "assignedAnesthesiologist",
      filterState.assignedAnesthesiologist,
    );
    assignCsvIf(p, "delayCategories", filterState.caseStartDelay);

    if (isBoolString(filterState.autoClosed)) {
      p.autoClosed = filterState.autoClosed;
    }

    const caseIds = normalizeIds(filterState.caseIds);
    assignIf(p, "caseIds", joinCsv(caseIds));

    // exclude
    assignCsvIf(p, "excludeOts", filterState.excludeOts);
    assignCsvIf(p, "excludeProcedureIds", filterState.excludeProcedureIds);
    assignCsvIf(
      p,
      "excludeAssignedAnesthesiologist",
      filterState.excludeAssignedAnesthesiologist,
    );
    assignCsvIf(
      p,
      "excludeDelayCategories",
      filterState.excludeDelayCategories,
    );
    assignIf(p, "excludeCaseOrder", filterState.excludeCaseOrder);

    assignCsvIf(
      p,
      "excludeAttendingSurgeonNames",
      filterState.excludeAttendingSurgeonNames,
    );
    assignCsvIf(p, "excludeNurseIds", filterState.excludeNurseIds);
    assignCsvIf(p, "excludeCaseIds", filterState.excludeCaseIds);

    return p;
  };

  const params = useMemo(
    () => buildCasesParams(filterState, shouldFetchCases, normalizeIds),
    [filterState, shouldFetchCases,normalizeIds],
  );

  const { data: casesData, isError } = useGetCasesQuery(
    params,
    shouldFetchCases,
    tenantId,
    hospitalId,
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleDropdownClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const applyTimerRef = useRef<number | null>(null);
  const syncDraftFromForm = useCallback(() => {
    const live = getValues();
    setDraftFilters(live);
  }, [getValues,setDraftFilters]);

  const handleApplyButtonClick = () => {
    const excludeFilters = buildExcludeFiltersFromAdvanced();
    const advancedFilters = buildAdvancedFilters(
      selectedAdvancedFilters,
      filterValueDropdown,
    );

    const live = getValues();

    const persisted = toPersistedFilterState({
      ...live,
      ...excludeFilters,
      advanceFilters: advancedFilters,
    });

    updateFilterValue(persisted);
    setIsFilterDrawerOpen(false); // ✅ drawer closes
    setApplyCount((prev) => prev + 1); // ✅ cases refetch
  };

  //save and apply saved filter
  const handleApplySavedFilter = (
    saved: SAVED_FILTER,
    isApplyAction: boolean = true,
  ) => {
    const start =
      saved?.dateRange?.startDate ??
      saved?.dateRange?.from ??
      saved?.dateRange?.start;

    const end =
      saved?.dateRange?.endDate ??
      saved?.dateRange?.to ??
      saved?.dateRange?.end;

    if (!start || !end) return;

    const nextDateRange = { from: new Date(start), to: new Date(end) };
    setPendingSavedFilter({ ...saved, __dateRange: nextDateRange });

    setValue("dateRange", nextDateRange);
    setValue("ots", []);
    setValue("procedureIds", []);
    setValue("attendingSurgeonNames", []);
    setValue("nurseIds", []);
    setValue("assignedAnesthesiologist", []);
    setValue("caseStartDelay", []);
    setValue("caseOrder", "");
    setValue("autoClosed", "");
    setValue("inconsistentTimestamps", "");
    setValue("antibioticUsed", "");
    setValue("caseIds", []);

    setSelectedAdvancedFilters([]);
    setFiltersIndexList([0]);

    setActiveTab("Filters");
    if (isApplyAction) {
      autoApplySavedRef.current = true;
      setIsApplying(true);
      setIsFilterDrawerOpen(true);
      setUpdateSavedFilterId(null);
    } else {
      setUpdateSavedFilterId(saved.uuid);
    }
  };

  const lastRawDateKeyRef = useRef<string>(
    (() => {
      const d = getFilterValue().dateRange;
      if (d?.from && d?.to) {
        return `${formatDate(d.from as Date)}_${formatDate(d.to as Date)}`;
      }
      return "";
    })(),
  );

  useEffect(() => {
    if (!rawDateParams) return;

    const key = `${rawDateParams.startDate}_${rawDateParams.endDate}`;
    if (lastRawDateKeyRef.current === key) return;

    lastRawDateKeyRef.current = key;

    if (pendingSavedFilter) return;

    setValue("ots", []);
    setValue("procedureIds", []);
    setValue("attendingSurgeonNames", []);
    setValue("nurseIds", []);
    setValue("assignedAnesthesiologist", []);
    setValue("caseStartDelay", []);
    setValue("caseIds", []);

    updateFilterValue({
      dateRange: watchedDateRange,
      ots: [],
      procedureIds: [],
      attendingSurgeonNames: [],
      nurseIds: [],
      assignedAnesthesiologist: [],
      caseStartDelay: [],
      caseIds: [],
    });
  }, [rawDateParams, pendingSavedFilter, setValue, watchedDateRange]);

  const EXCLUDE_KEY_BY_FIELD = useMemo(
    () =>
      ({
        otNames: "excludeOts",
        procedureIds: "excludeProcedureIds",
        attendingSurgeonNames: "excludeAttendingSurgeonNames",
        primarySurgeons: "excludeAttendingSurgeonNames",
        circulatingNurses: "excludeNurseIds",
        anesthesiologists: "excludeAssignedAnesthesiologist",
        caseStartDelays: "excludeDelayCategories",
        caseOrder: "excludeCaseOrder",
        caseIds: "excludeCaseIds",
      }) as Record<string, string>,
    [],
  );

  const resolveOtIdsFromNames = (savedOtNames: string[], otsData: any[]) => {
    const wanted = new Set(
      savedOtNames.map((x) => String(x).trim().toLowerCase()),
    );
    return (otsData || [])
      .filter((ot: any) => wanted.has(String(ot.label).trim().toLowerCase()))
      .map((ot: any) => ot.value);
  };

  const buildAdvancedFiltersUI = (advanceFilters?: any[]) => {
    if (!Array.isArray(advanceFilters) || !advanceFilters.length) {
      return [{ filterType: "", condition: "", selectedValues: [] }];
    }
    return advanceFilters.map((af: any) => {
      const field = af.field || af.filterType;
      const values =
        (af.values || af.selectedValues)?.map((v: any) => v.id ?? v) || [];

      return {
        filterType: field,
        condition: af.operator || af.condition || "notEquals",
        selectedValues: field === "caseOrder" ? (values[0] ?? "") : values,
      };
    });
  };

  const buildExcludeState = useCallback(
    (advanceFilters?: any[]) => {
      const excludeState: any = {};
      if (!Array.isArray(advanceFilters)) return excludeState;

      advanceFilters.forEach((af: any) => {
        const ids = af.values?.map((v: any) => v.id).filter(Boolean) || [];
        if (!ids.length) return;

        const excludeKey = EXCLUDE_KEY_BY_FIELD[af.field];
        if (!excludeKey) return;

        excludeState[excludeKey] =
          af.field === "caseOrder" ? (ids[0] ?? "") : ids;
      });

      return excludeState;
    },
    [EXCLUDE_KEY_BY_FIELD],
  );

  const applyAdvancedFiltersToForm = (
    setValue: any,
    advancedFiltersUI: any[],
  ) => {
    advancedFiltersUI.forEach((af, index) => {
      setValue(`type_${index}`, af.filterType);
      setValue(`condition_${index}`, af.condition);
      setValue(`options_${index}`, af.selectedValues);
    });
  };
  const pendingFilterTimeoutRef = useRef<number | null>(null);

  const currentRangeKey = useMemo(() => {
    if (!dateParams?.startDate || !dateParams?.endDate) return "";
    return `${dateParams.startDate}_${dateParams.endDate}`;
  }, [dateParams]);
  const autoApplySavedRef = useRef(false);

  useEffect(() => {
    if (!pendingSavedFilter) return;

    const pendingKey = getRangeKey(
      pendingSavedFilter.__dateRange.from,
      pendingSavedFilter.__dateRange.to,
    );

    if (currentRangeKey !== pendingKey) return;

    if (needsOtsButMissing(pendingSavedFilter, otsData)) return;

    if (needsProceduresButMissing(pendingSavedFilter, procedureListData))
      return;

    const { names: savedOtNames, ids: resolvedOtIds } = resolveOtIdsSafe(
      pendingSavedFilter,
      otsData,
      resolveOtIdsFromNames,
    );

    if (savedOtNames.length > 0 && resolvedOtIds.length === 0) {
      console.warn("Saved filter OT names did not match any current OTs");
    }

    const resolvedProcedureIds = resolveProcedureIdsFromSaved(
      (pendingSavedFilter.procedureIds ?? []) as string[],
      procedureListData ?? [],
    );

    const advancedFiltersUI = buildAdvancedFiltersUI(
      pendingSavedFilter.advanceFilters,
    );
    applyAdvancedFiltersToForm(setValue as any, advancedFiltersUI);

    setSelectedAdvancedFilters(advancedFiltersUI);
    setFiltersIndexList(advancedFiltersUI.map((_: any, i: number) => i));

    const excludeState = buildExcludeState(pendingSavedFilter.advanceFilters);
    const nextState = buildNextStateFromPending(
      pendingSavedFilter,
      resolvedOtIds,
      resolvedProcedureIds,
      excludeState,
    );

    setValue("ots", nextState.ots);
    setValue("procedureIds", nextState.procedureIds);
    setValue("attendingSurgeonNames", nextState.attendingSurgeonNames);
    setValue("nurseIds", nextState.nurseIds);
    setValue("assignedAnesthesiologist", nextState.assignedAnesthesiologist);
    setValue("caseStartDelay", nextState.caseStartDelay);
    setValue("caseOrder", nextState.caseOrder);
    setValue("autoClosed", nextState.autoClosed);
    setValue("inconsistentTimestamps", nextState.inconsistentTimestamps);
    setValue("antibioticUsed", nextState.antibioticUsed);
    setValue("caseIds", nextState.caseIds);

    queueMicrotask(syncDraftFromForm);

    if (autoApplySavedRef.current) {
      autoApplySavedRef.current = false;
      updateFilterValue(nextState);
      setApplyCount((prev) => prev + 1);
      setIsFilterDrawerOpen(false);
    }

    setIsApplying(false);

    if (pendingFilterTimeoutRef.current) {
      clearTimeout(pendingFilterTimeoutRef.current);
    }
    pendingFilterTimeoutRef.current = window.setTimeout(() => {
      setPendingSavedFilter(null);
    }, 300);

    return () => {
      if (pendingFilterTimeoutRef.current) {
        clearTimeout(pendingFilterTimeoutRef.current);
      }
    };
  }, [
    pendingSavedFilter,
    otsData,
    procedureListData,
    setValue,
    currentRangeKey,
    syncDraftFromForm,
    buildExcludeState,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    const timer = applyTimerRef.current;
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, []);

  useEffect(() => {
    if (!isApplying) return;

    if (pendingSavedFilter) return;

    if (casesData) {
      setIsApplying(false);
      setIsFilterDrawerOpen(false);
    }

    if (isError) {
      setIsApplying(false);
    }
  }, [casesData, isError, isApplying, pendingSavedFilter]);

  const buildExcludeFiltersFromAdvanced = () => {
    const excludeState: any = {};

    selectedAdvancedFilters.forEach((filter: any) => {
      if (!filter.filterType || !filter.selectedValues?.length) return;

      const dropdownValues =
        filterValueDropdown.find((item: any) => item.key === filter.filterType)
          ?.value || [];

      const resolvedIds = dropdownValues
        .filter(
          (item: any) => filter.selectedValues.includes(item.key), // value from UI
        )
        .map((item: any) => item.key);

      if (!resolvedIds.length) return;

      switch (filter.filterType) {
        case "otNames":
          excludeState.excludeOts = resolvedIds;
          break;

        case "procedureIds":
          excludeState.excludeProcedureIds = resolvedIds;
          break;

        case "attendingSurgeonNames":
          excludeState.excludeAttendingSurgeonNames = resolvedIds;
          break;

        case "circulatingNurses":
          excludeState.excludeNurseIds = resolvedIds;
          break;

        case "anesthesiologists":
          excludeState.excludeAssignedAnesthesiologist = resolvedIds;
          break;

        case "caseStartDelays":
          excludeState.excludeDelayCategories = resolvedIds;
          break;

        case "caseOrder":
          excludeState.excludeCaseOrder = resolvedIds[0] ?? "";
          break;
        case "caseIds":
          excludeState.excludeCaseIds = resolvedIds;
          break;

        default:
          break;
      }
    });

    return excludeState;
  };
  const [limitErrorOpen, setLimitErrorOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");

  const addDateRange = (data: any, target: any) => {
    if (data.dateRange?.from && data.dateRange?.to) {
      target.dateRange = {
        startDate: data.dateRange.from,
        endDate: data.dateRange.to,
      };
    }
  };

  const addOtNames = (data: any, target: any, otsData: any[]) => {
    if (!data.ots?.length) return;

    target.otNames = data.ots.map((otId: string) => {
      const foundOT = otsData?.find((ot: any) => ot.value === otId);
      return foundOT?.label ?? "";
    });
  };

  const addProcedureNames = (data: any, target: any, proceduresList: any[]) => {
    if (!Array.isArray(data.procedureIds) || data.procedureIds.length === 0) {
      return;
    }

    const names = data.procedureIds
      .map((id: string) => {
        const found = proceduresList.find(
          (p: any) => String(p.key) === String(id),
        );
        return found?.value ?? String(id);
      })
      .filter((name: string) => !!String(name).trim());

    if (names.length) {
      target.procedureIds = names;
    }
  };

  const addIfNonEmpty = (value: any, key: string, target: any) => {
    if (Array.isArray(value) && value.length > 0) {
      target[key] = value;
    }
  };

  const buildAdvancedFilters = useCallback(
    (selectedAdvancedFilters: any[], filterValueDropdown: any[]) => {
      if (!Array.isArray(selectedAdvancedFilters)) return [];

      return selectedAdvancedFilters
        .map((filter) => {
          const hasValue = Array.isArray(filter.selectedValues)
            ? filter.selectedValues.length > 0
            : filter.selectedValues !== undefined &&
              filter.selectedValues !== null &&
              filter.selectedValues !== "";

          if (!filter.filterType || !filter.condition || !hasValue) {
            return null;
          }

          const dropdownValues =
            filterValueDropdown.find(
              (item: any) => item.key === filter.filterType,
            )?.value ?? [];

          const selectedKeys = Array.isArray(filter.selectedValues)
            ? filter.selectedValues
            : [filter.selectedValues];

          const values = dropdownValues
            .filter((item: any) => selectedKeys.includes(item.key))
            .map((item: any) => ({
              id: item.key,
              name: item.value,
            }));

          return values.length
            ? {
                field: filter.filterType,
                operator: filter.condition,
                values,
              }
            : null;
        })
        .filter(Boolean);
    },
    [],
  );

  const normalizeCaseIdsForSave = useCallback((v: any): string[] => {
    if (!Array.isArray(v)) return [];
    return v
      .map((x) => (typeof x === "string" ? x : x?.key ?? x?.value))
      .filter(Boolean);
  }, []);

  const addCaseMeta = (data: any, target: any) => {
    // autoClosed stored as "true"/"false" in UI
    if (data.autoClosed === "true" || data.autoClosed === "false") {
      target.autoClosed = data.autoClosed === "true";
    }

    if (
      data.inconsistentTimestamps === "true" ||
      data.inconsistentTimestamps === "false"
    ) {
      target.inconsistentTimestamps = data.inconsistentTimestamps === "true";
    }

    if (data.antibioticUsed === "true" || data.antibioticUsed === "false") {
      target.antibioticUsed = data.antibioticUsed === "true";
    }

    if (data.caseOrder === "first" || data.caseOrder === "last") {
      target.caseOrder = data.caseOrder;
    }

    const ids = normalizeCaseIdsForSave(data.caseIds);
    if (ids.length) target.caseIds = ids;
  };
  const { mutate: createSavedFilter } = useCreateSavedFilterMutation(
    tenantId,
    hospitalId,
  );
  const [startEditingFilterId, setStartEditingFilterId] = useState<
    string | null
  >(null);
  const onSave = (data: any) => {
    if (isMaxLimitReached) {
      setSnackbarMessage(t("Max_selection_5"));
      setLimitErrorOpen(true);
      return;
    }

    const filterData: any = { filterName: "Custom Filter" };

    addDateRange(data, filterData);
    addOtNames(data, filterData, otsData || []);

    addProcedureNames(data, filterData, proceduresList || []);
    addIfNonEmpty(data.attendingSurgeonNames, "primarySurgeons", filterData);
    addIfNonEmpty(data.nurseIds, "circulatingNurses", filterData);
    addIfNonEmpty(data.assignedAnesthesiologist, "anesthesiologists", filterData);
    addIfNonEmpty(data.caseStartDelay, "caseStartDelays", filterData);
    addCaseMeta(data, filterData);
    const advancedFilters = buildAdvancedFilters(
      selectedAdvancedFilters,
      filterValueDropdown,
    );
    if (advancedFilters.length) {
      filterData.advanceFilters = advancedFilters;
    }

    createSavedFilter(filterData, {
      onSuccess: (res: any) => {
        const savedId = res?.uuid ?? res?.id ?? null;

        handleReset();

        setStartEditingFilterId(savedId);
        setActiveTab("Saved_Filters");
      },
    });
  };

  useEffect(() => {
    if (activeTab !== "Saved_Filters" && startEditingFilterId) {
      setStartEditingFilterId(null);
    }
  }, [activeTab, startEditingFilterId]);
  const handleReset = () => {
    setUpdateSavedFilterId(null);
    const last7Days = getLast7DaysRange();

    const resetState = {
      ...initialFilterState,
      dateRange: last7Days,

      caseOrder: "",
      autoClosed: "",
      inconsistentTimestamps: "",
      antibioticUsed: "",

      // optional: also clear ids explicitly (if initialFilterState has defaults)
      ots: [],
      procedureIds: [],
      attendingSurgeonNames: [],
      nurseIds: [],
      assignedAnesthesiologist: [],
      caseStartDelay: [],
      caseIds: [],

      // exclude fields
      excludeOts: [],
      excludeProcedureIds: [],
      excludeAttendingSurgeonNames: [],
      excludeNurseIds: [],
      excludeAssignedAnesthesiologist: [],
      excludeDelayCategories: [],
      excludeCaseOrder: "",
      excludeCaseIds: [],
    };

    reset(resetState);

    updateFilterValue(resetState);

    setQuickDays(7);

    setFiltersIndexList([0]);
    setSelectedAdvancedFilters([
      { filterType: "", condition: "", selectedValues: [] },
    ]);

    setDraftFilters({
      ...resetState,
      type_0: "",
      condition_0: "",
      options_0: [],
    });
  };
  const asArray = <T,>(v: any): T[] => (Array.isArray(v) ? v : []);

  const normalizeCaseId = (x: any) =>
    typeof x === "string" ? x : (x?.key ?? x?.value);

  const normalizeCaseIds = (v: any): string[] =>
    asArray<any>(v).map(normalizeCaseId).filter(Boolean);

  const toPersistedFilterState = (data: any) => {
    return {
      dateRange: data?.dateRange,
      ots: asArray<string>(data?.ots),
      procedureIds: asArray<string>(data?.procedureIds),
      attendingSurgeonNames: asArray<string>(data?.attendingSurgeonNames),
      nurseIds: asArray<string>(data?.nurseIds),
      assignedAnesthesiologist: asArray<string>(data?.assignedAnesthesiologist),
      caseStartDelay: asArray<string>(data?.caseStartDelay),
      caseIds: normalizeCaseIds(data?.caseIds),

      caseOrder: data?.caseOrder ?? "",
      autoClosed: data?.autoClosed ?? "",
      inconsistentTimestamps: data?.inconsistentTimestamps ?? "",
      antibioticUsed: data?.antibioticUsed ?? "",

      excludeOts: asArray<string>(data?.excludeOts),
      excludeProcedureIds: asArray<string>(data?.excludeProcedureIds),
      excludeAttendingSurgeonNames: asArray<string>(
        data?.excludeAttendingSurgeonNames,
      ),
      excludeNurseIds: asArray<string>(data?.excludeNurseIds),
      excludeAssignedAnesthesiologist: asArray<string>(
        data?.excludeAssignedAnesthesiologist,
      ),
      excludeDelayCategories: asArray<string>(data?.excludeDelayCategories),
      excludeCaseOrder: data?.excludeCaseOrder ?? "",
      excludeCaseIds: asArray<string>(data?.excludeCaseIds),
      advanceFilters: data?.advanceFilters ?? [],
    };
  };

  const watchedValues: any = useWatch({ control });
  useEffect(() => {
    if (pendingSavedFilter) return;
    setDraftFilters(watchedValues);
  }, [watchedValues, pendingSavedFilter, setDraftFilters]);

  const buildAdvancedFilter = useCallback(
    (index: number) => {
      return {
        filterType: watchedValues?.[`type_${index}`] ?? "",
        condition: watchedValues?.[`condition_${index}`] ?? "",
        selectedValues: watchedValues?.[`options_${index}`] ?? [],
      };
    },
    [watchedValues],
  );

  const upsertAdvancedFilter = useCallback(
    (index: number) => {
      const newFilter = buildAdvancedFilter(index);
      setSelectedAdvancedFilters((prev) => {
        const updated: any = [...prev];

        updated[index] = newFilter;

        return updated;
      });
    },
    [buildAdvancedFilter, setSelectedAdvancedFilters],
  );

  const handleAddFilter = () => {
    setFiltersIndexList((prev) => [...prev, prev.length]);
    const defaultFilter = {
      filterType: "",
      condition: "",
      selectedValues: [],
    };
    setSelectedAdvancedFilters((prev) => {
      let _selected: any = [...prev];

      _selected.push(defaultFilter);
      return _selected;
    });
  };

  const handleDeleteFilter = (indexToRemove: number) => {
    const currentCount = filtersIndexList.length;

    // Shift form values
    for (let i = indexToRemove; i < currentCount - 1; i++) {
      const nextIndex = i + 1;
      // @ts-ignore
      const nextType = getValues(`type_${nextIndex}`);
      // @ts-ignore
      const nextCondition = getValues(`condition_${nextIndex}`);
      // @ts-ignore
      const nextOptions = getValues(`options_${nextIndex}`);

      // @ts-ignore
      setValue(`type_${i}`, nextType);
      // @ts-ignore
      setValue(`condition_${i}`, nextCondition);
      // @ts-ignore
      setValue(`options_${i}`, nextOptions);
    }

    // Unregister last
    const lastIndex = currentCount - 1;
    // @ts-ignore
    unregister(`type_${lastIndex}`);
    // @ts-ignore
    unregister(`condition_${lastIndex}`);
    // @ts-ignore
    unregister(`options_${lastIndex}`);

    setFiltersIndexList((prev) => {
      if (!prev) return [];
      const newLength = prev.length - 1;
      return Array.from({ length: newLength }, (_, i) => i);
    });

    setSelectedAdvancedFilters((prev: any) =>
      prev?.filter((_: any, i: number) => i !== indexToRemove),
    );

    setSnackbarMessage("Filter deleted successfully");
    setLimitErrorOpen(true);
  };

  useEffect(() => {
    // If filtersIndexList changes, it implies we added/removed filters or loaded from saved.
    // However, if we loaded from saved, we already set selectedAdvancedFilters correctly.
    // We should only upsert if the watched values actually changed relative to what we have?
    // Or simpler: if we are loading a saved filter, maybe we should skip this effect?
    if (pendingSavedFilter) return;

    filtersIndexList.forEach((index) => upsertAdvancedFilter(index));
  }, [watchedValues, filtersIndexList, pendingSavedFilter, upsertAdvancedFilter]);

  useEffect(() => {
    const { from, to } = dashboardFilterDateRange || {};
    const { dateRange, quickDays } = calculateCalendarDefaults(from, to);
    setValue("dateRange", dateRange);
    setQuickDays(quickDays);
  }, [dashboardFilterDateRange, setValue]);

  const mappedSavedFilters = useMemo<SAVED_FILTER[]>(() => {
    return (
      (savedFiltersData ?? []).map((filter: any) => ({
        uuid: filter.uuid,
        filterName: filter.filterName,
        dateRange: filter.dateRange,
        otNames: filter.otNames,
        procedureIds: filter.procedureIds,
        primarySurgeons: filter.primarySurgeons || filter.attendingSurgeonNames,
        circulatingNurses: filter.circulatingNurses || filter.nurseIds,
        anesthesiologists:
          filter.anesthesiologists || filter.assignedAnesthesiologist,
        caseStartDelays: filter.caseStartDelays || filter.caseStartDelay,
        caseOrder: filter.caseOrder,
        autoClosed: filter.autoClosed,
        caseIds: filter.caseIds,
        isDefault: filter?.isDefault || false,
        advanceFilters: filter?.advanceFilters,
      })) ?? []
    );
  }, [savedFiltersData]);

  const defaultSavedFilter = useMemo<SAVED_FILTER | null>(() => {
    return (
      mappedSavedFilters.find((f) => f.isDefault) ||
      mappedSavedFilters[0] ||
      null
    );
  }, [mappedSavedFilters]);

  const otLabelById = useMemo(() => {
  const m = new Map<string, string>();
  (otsData ?? []).forEach((ot: any) => m.set(String(ot.value), String(ot.label ?? "")));
  return m;
}, [otsData]);

const procedureLabelById = useMemo(() => {
  const m = new Map<string, string>();
  (proceduresList ?? []).forEach((p: any) => m.set(String(p.key), String(p.value ?? "")));
  return m;
}, [proceduresList]);

const handleUpdateSavedFilter = useCallback(
  (data: any) => {
    if (!updateSavedFilterId) return;

    const payload: any = {
      dateRange:
        data?.dateRange?.from && data?.dateRange?.to
          ? { startDate: data.dateRange.from, endDate: data.dateRange.to }
          : null,

      otNames: mapIdsToLabelsOrNull(data?.ots, otLabelById),

      procedureIds: mapProcIdsToLabelsOrNull(data?.procedureIds, procedureLabelById),

      primarySurgeons: toNullIfEmptyArr(data?.attendingSurgeonNames),

      circulatingNurses: toNullIfEmptyArr(data?.nurseIds),

      anesthesiologists: toNullIfEmptyArr(data?.assignedAnesthesiologist),

      caseStartDelays: toNullIfEmptyArr(data?.caseStartDelay),

      autoClosed: toNullableBool(data?.autoClosed),

      caseOrder: toNullableCaseOrder(data?.caseOrder),

      caseIds: (() => {
        const ids = normalizeCaseIdsForSave(data?.caseIds);
        return ids.length ? ids : null;
      })(),

      advanceFilters: (() => {
        const af = buildAdvancedFilters(selectedAdvancedFilters, filterValueDropdown);
        return af.length ? af : null;
      })(),
    };

    updateSavedFilter(
      { filterId: updateSavedFilterId, ...payload },
      {
        onSuccess: () => {
          setUpdateSavedFilterId(null);
          setActiveTab("Saved_Filters");
        },
      },
    );
  },
  [
    updateSavedFilterId,
    updateSavedFilter,
    otLabelById,
    procedureLabelById,
    selectedAdvancedFilters,
    filterValueDropdown,
    normalizeCaseIdsForSave, 
    buildAdvancedFilters,  
  ],
);



  const handleFooterApply = () => {
    if (updateSavedFilterId) {
      handleSubmit(handleUpdateSavedFilter)();
      return;
    }

    if (savedFilters) {
      if (defaultSavedFilter) handleApplySavedFilter(defaultSavedFilter);
      else {
        setIsApplying(false);
        setIsFilterDrawerOpen(false);
      }
      return;
    }

    handleApplyButtonClick(); // ✅ no extra setIsApplying
  };

  const handleFooterReset = () => {
    if (savedFilters) {
      if (defaultSavedFilter) {
        handleApplySavedFilter(defaultSavedFilter);
      }
      return;
    }
    handleReset();
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={isFilterDrawerOpen}
        onClose={handleDrawerClose}
        ModalProps={{ disableEscapeKeyDown: true }}
        slotProps={{
          paper: {
            sx: {
              width: 600,
              transition: "width 0.3s ease",
              borderRadius: "5px 0 0 5px",
              overflow: "auto",
              backgroundColor: "custom.background.secondary",
            },
          },
        }}
      >
        <Box p={3} display="flex" flexDirection="column" flex={1}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <CustomTabs
              tabs={tabData}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              variant="segmented"
            />
            <IconButton
              onClick={() => {
                setActiveTab("Filters");
                if(updateSavedFilterId){
                  handleReset();
                }
                setIsFilterDrawerOpen(false);
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Body */}
          <Box mt={3} gap={2} display="flex" flexDirection="column">
            {filters && (
              <DashboardFiltersTab
                control={control}
                watch={watch}
                quickDays={quickDays}
                otsList={otsList}
                proceduresList={proceduresList}
                surgeonsList={surgeonsList}
                anesthesiologistList={anesthesiologistList}
                circulatingNurseList={circulatingNurseList}
                caseIdListData={caseIdData}
                caseStartDelayList={caseStartDelayList}
                handleReset={handleReset}
                selectedAdvancedFilters={selectedAdvancedFilters}
                setSelectedAdvancedFilters={setSelectedAdvancedFilters}
                handleAddFilter={handleAddFilter}
                handleDeleteFilter={handleDeleteFilter}
                setFilterValueDropdown={setFilterValueDropdown}
                setValue={setValue as any}
                isPopulating={!!pendingSavedFilter}
                isSurgeon={isSurgeon}
              />
            )}
            {savedFilters && (
              <DashboardSavedFiltersTab
                applyFilter={handleApplySavedFilter}
                startEditingFilterId={startEditingFilterId}
                onEditDone={() => setStartEditingFilterId(null)}
              />
            )}
          </Box>
        </Box>

        {/* Footer */}
        <Box mt="auto" mb={3}>
          {savedFilters && (
            <Box sx={{ display: "flex", alignItems: "center", mb: 1, ml: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Icons.InfoBlue />
              </Box>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "black",
                  marginTop: 0,
                  marginLeft: 0.5,
                }}
              >
                {t("Max_selection_5")}
              </Typography>
            </Box>
          )}
          {activeTab !== "Saved_Filters" && (
            <>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" justifyContent="space-between" gap={2} px={3}>
                <Box flex={1} display="flex">
                  <Button
                    variant="contained"
                    disabled={isApplying}
                    sx={{
                      flex: 1,
                      height: 40,
                      backgroundColor: "custom.background.brand",
                      fontWeight: 500,
                      fontSize: 14,
                      borderRadius: 100,
                      textTransform: "none",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 1,
                      minWidth: 0,
                    }}
                  >
                    {isApplying ? (
                      <CircularProgress size={18} sx={{ color: "white" }} />
                    ) : (
                      <Typography
                        onClick={handleFooterApply}
                        sx={{
                          flex: 1,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          color: "white",
                          fontWeight: 500,
                          fontSize: 14,
                        }}
                      >
                        {updateSavedFilterId
                          ? t("Update_Filters")
                          : t("Apply_Filters")}
                      </Typography>
                    )}
                    {filters && (
                      <>
                        <Typography>|</Typography>
                        <IconButton
                          sx={{ padding: 0 }}
                          id="save-filter-menu-button"
                          onClick={handleDropdownClick}
                        >
                          <ArrowDropDownIcon
                            sx={{ fontSize: 18, color: "white" }}
                          />
                        </IconButton>
                      </>
                    )}
                  </Button>
                  <Menu
                    anchorEl={anchorEl}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    open={Boolean(anchorEl)}
                    onClose={handleCloseMenu}
                  >
                    <MenuItem
                      onClick={() => {
                        handleCloseMenu();
                        handleSubmit(onSave)();
                      }}
                    >
                      {t("Save_Filter")}
                    </MenuItem>
                  </Menu>
                </Box>
                {!updateSavedFilterId ? (
                  <Box flex={1} display="flex">
                    <Button
                      variant="outlined"
                      disabled={isApplying}
                      onClick={handleFooterReset}
                      sx={{
                        flex: 1,
                        px: 3.5,
                        height: 40,
                        color: "custom.background.brand",
                        fontWeight: 500,
                        fontSize: 14,
                        border: "1px solid",
                        borderColor: "custom.background.brand",
                        borderRadius: 100,
                        textTransform: "none",
                        minWidth: 0,
                      }}
                    >
                      {t("Reset_All")}
                    </Button>
                  </Box>
                ) : null}
              </Box>
            </>
          )}
        </Box>
      </Drawer>
      <Snackbar
        open={limitErrorOpen}
        autoHideDuration={3000}
        onClose={() => setLimitErrorOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setLimitErrorOpen(false)}
          severity="info"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage || t("Max_selection_5")}
        </Alert>
      </Snackbar>
    </>
  );
};
export default DashboardFilterDrawer;
